import { NDA_ACCEPTANCE_CHECKBOX_LABELS } from './nda-acceptance'

export const RISK_PROFILER_COMPLETED_KEY = 'risk_profiler_completed'
export const RISK_PROFILER_ANSWERS_KEY = 'risk_profiler_answers'

export type RiskQuestionIcon =
  | 'target'
  | 'volatility'
  | 'horizon'
  | 'experience'
  | 'reaction'
  | 'allocation'
  | 'liquidity'
  | 'knowledge'
  | 'understanding'

export type RiskQuestionOption = {
  id: string
  label: string
  score: number
  /** Optional guidance shown under the option label. */
  description?: string
}

export type RiskQuestion = {
  id: string
  icon: RiskQuestionIcon
  question: string
  /** Optional helper copy shown under the question. */
  guidance?: string
  /** When false, the answer is stored but excluded from totalScore. Default true. */
  scored?: boolean
  /** When true, more than one option can be selected. Stored as string[]. */
  multiSelect?: boolean
  options: RiskQuestionOption[]
}

export type RiskAnswerValue = string | string[]
export type RiskProfilerAnswers = Record<string, RiskAnswerValue>

export type RiskProfileBand = 'low' | 'moderate_low' | 'moderate' | 'moderate_high' | 'high'

export type RiskProfileDeclarationId =
  | 'confidentiality'
  | 'no_offer'
  | 'kyc_documents'
  | 'adverse_media'

export type RiskProfileDeclaration = {
  id: RiskProfileDeclarationId
  text: string
  agreed: true
  agreed_at: string
}

export type RiskProfileAnswersPayload = {
  answers: RiskProfilerAnswers
  /** Sum of option scores (min 25, max 100). */
  totalScore: number | null
  riskBand: RiskProfileBand | null
  declarations: RiskProfileDeclaration[]
}

export type RiskProfilePatchBody = {
  risk_profile_completed: true
  risk_profile_answers: RiskProfileAnswersPayload
}

export type RiskProfileDisplay = {
  completed: boolean
  answers: RiskProfilerAnswers
  totalScore: number | null
  riskBand: RiskProfileAnswersPayload['riskBand']
  declarations?: RiskProfileDeclaration[]
}

const RISK_BAND_LABELS: Record<RiskProfileBand, string> = {
  low: 'Conservative',
  moderate_low: 'Moderate',
  moderate: 'Balanced',
  moderate_high: 'Growth',
  high: 'Aggressive',
}

/** Theoretical score range for the five scored questions (5 × 5–20). */
export const RISK_SCORE_MIN = 25
export const RISK_SCORE_MAX = 100

export const RISK_PROFILER_QUESTIONS: RiskQuestion[] = [
  {
    id: 'investment_horizon',
    icon: 'horizon',
    question: 'What is your intended investment horizon?',
    guidance:
      'Investment horizon is the amount of time you expect to hold an investment prior to a liquidity\u00a0event.',
    options: [
      { id: 'horizon_under_1', label: 'Less than 1 year', score: 5 },
      { id: 'horizon_1_3', label: '1–3 years', score: 10 },
      { id: 'horizon_3_5', label: '3–5 years', score: 15 },
      { id: 'horizon_5_10', label: '5–10 years', score: 20 },
    ],
  },
  {
    id: 'risk_tolerance',
    icon: 'volatility',
    question:
      'What best describes your risk tolerance for your investing activities through Credibila?',
    options: [
      {
        id: 'risk_conservative',
        label: 'Conservative',
        score: 5,
        description:
          'You prioritize protecting your original investment over growing it. Stability matters more to you than high returns, and you would rather accept modest, steady gains than risk meaningful losses. You prefer investments that are easy to price and quick to sell if needed, though you are occasionally open to a smaller, less liquid position that carries somewhat more risk.',
      },
      {
        id: 'risk_moderate',
        label: 'Moderate',
        score: 10,
        description:
          'You are comfortable balancing growth and stability, willing to accept some short-term ups and downs in exchange for better long-term returns. You are not chasing maximum growth, but you can tolerate moderate portfolio swings and are open to a mix of liquid and somewhat less liquid investments.',
      },
      {
        id: 'risk_aggressive',
        label: 'Aggressive',
        score: 15,
        description:
          'Growth is your priority, and you are willing to accept significant volatility, including the possibility of large losses, in pursuit of higher long-term returns. You are comfortable holding investments that may be harder to sell quickly or take longer to realize value.',
      },
      {
        id: 'risk_significant',
        label: 'Significant Risk',
        score: 20,
        description:
          'You are seeking the highest possible returns and are willing to accept substantial, even total, loss of your investment as a trade-off. You understand that the investments suited to this profile may be highly illiquid, or difficult to value, and you are comfortable with that level of uncertainty.',
      },
    ],
  },
  {
    id: 'investing_experience_duration',
    icon: 'experience',
    question: 'How long have you been investing?',
    options: [
      { id: 'exp_none', label: 'No experience', score: 5 },
      { id: 'exp_1_3', label: '1–3 years', score: 10 },
      { id: 'exp_3_5', label: '3–5 years', score: 15 },
      { id: 'exp_over_5', label: 'More than 5 years', score: 20 },
    ],
  },
  {
    id: 'investing_knowledge',
    icon: 'knowledge',
    question: 'What best describes your level of investing knowledge?',
    options: [
      { id: 'knowledge_none', label: 'No knowledge', score: 5 },
      { id: 'knowledge_limited', label: 'Limited', score: 10 },
      { id: 'knowledge_good', label: 'Good', score: 15 },
      { id: 'knowledge_sophisticated', label: 'Sophisticated', score: 20 },
    ],
  },
  {
    id: 'previous_investment_types',
    icon: 'knowledge',
    question: 'What types of investments have you previously held?',
    guidance: 'Select all that apply. When you are done, tap Next to continue.',
    multiSelect: true,
    options: [
      { id: 'held_cash', label: 'Cash/deposits', score: 5 },
      { id: 'held_bonds_funds', label: 'Bonds or mutual funds', score: 10 },
      { id: 'held_equities_etfs', label: 'Public equities or ETFs', score: 15 },
      {
        id: 'held_pe_vc_complex',
        label: 'Private equity, venture capital or complex products',
        score: 20,
      },
    ],
  },
  {
    id: 'expected_transactional_value',
    icon: 'allocation',
    question: 'Your initial investment limit is $1,000. Would you like a higher limit from day 1?',
    scored: false,
    options: [
      { id: 'txn_up_to_1000', label: 'Skip for now', score: 0 },
      { id: 'txn_above_1000', label: 'I want a higher limit', score: 0 },
    ],
  },
]

export const RISK_PROFILER_HIGHER_LIMIT_OPTION_ID = 'txn_above_1000'
export const RISK_PROFILER_SKIP_LIMIT_OPTION_ID = 'txn_up_to_1000'
export const RISK_PROFILER_TRANSACTION_QUESTION_ID = 'expected_transactional_value'
export const RISK_PROFILER_SKIP_LIMIT_DISCLAIMER =
  'If you skip for now, the initial limit of $1000 will apply. You can upload statements anytime via your dashboard to increase your investment limit.'

export function requiresBankStatementUpload(answers: RiskProfilerAnswers): boolean {
  return answers[RISK_PROFILER_TRANSACTION_QUESTION_ID] === RISK_PROFILER_HIGHER_LIMIT_OPTION_ID
}

export function isSkipLimitSelected(answers: RiskProfilerAnswers): boolean {
  return answers[RISK_PROFILER_TRANSACTION_QUESTION_ID] === RISK_PROFILER_SKIP_LIMIT_OPTION_ID
}

export function getRiskProfilerCompletedFromStorage(userId: number): boolean {
  if (!userId) return false
  try {
    return localStorage.getItem(`${RISK_PROFILER_COMPLETED_KEY}_${userId}`) === 'true'
  } catch {
    return false
  }
}

export function setRiskProfilerCompletedInStorage(userId: number, completed: boolean): void {
  if (!userId) return
  try {
    const key = `${RISK_PROFILER_COMPLETED_KEY}_${userId}`
    if (completed) {
      localStorage.setItem(key, 'true')
    } else {
      localStorage.removeItem(key)
    }
  } catch {
    // ignore storage errors
  }
}

export function saveRiskProfilerAnswersInStorage(
  userId: number,
  answers: RiskProfilerAnswers,
): void {
  if (!userId) return
  try {
    localStorage.setItem(`${RISK_PROFILER_ANSWERS_KEY}_${userId}`, JSON.stringify(answers))
  } catch {
    // ignore storage errors
  }
}

export function getRiskProfilerAnswersFromStorage(userId: number): RiskProfilerAnswers {
  if (!userId) return {}
  try {
    const raw = localStorage.getItem(`${RISK_PROFILER_ANSWERS_KEY}_${userId}`)
    if (!raw) return {}
    return JSON.parse(raw) as RiskProfilerAnswers
  } catch {
    return {}
  }
}

export function getAnswerOptionIds(value: RiskAnswerValue | undefined): string[] {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value) return [value]
  return []
}

export function isQuestionAnswered(question: RiskQuestion, answers: RiskProfilerAnswers): boolean {
  return getAnswerOptionIds(answers[question.id]).length > 0
}

export function toggleRiskAnswer(
  answers: RiskProfilerAnswers,
  question: RiskQuestion,
  optionId: string,
): RiskProfilerAnswers {
  if (!question.multiSelect) {
    return { ...answers, [question.id]: optionId }
  }

  const current = getAnswerOptionIds(answers[question.id])
  const nextIds = current.includes(optionId)
    ? current.filter((id) => id !== optionId)
    : [...current, optionId]

  return { ...answers, [question.id]: nextIds }
}

function getQuestionScore(question: RiskQuestion, answers: RiskProfilerAnswers): number | null {
  const selectedIds = getAnswerOptionIds(answers[question.id])
  if (selectedIds.length === 0) return null

  const scores = selectedIds.map((id) => question.options.find((option) => option.id === id)?.score)
  if (scores.some((score) => score === undefined)) return null
  if (question.scored === false) return 0
  return Math.max(...(scores as number[]))
}

/** Total option score (higher = higher risk capacity / tolerance). */
export function calculateRiskProfileScore(answers: RiskProfilerAnswers): number | null {
  const resolved = RISK_PROFILER_QUESTIONS.map((question) => getQuestionScore(question, answers))
  if (resolved.some((score) => score === null)) return null
  return resolved.reduce<number>((sum, score) => sum + (score as number), 0)
}

/**
 * Map total score (20–100 sheet) to platform risk band.
 * 20–39 Low, 40–54 Moderate-Low, 55–69 Moderate, 70–84 Moderate-High, 85–100 High.
 */
export function getRiskProfileBand(score: number): RiskProfileBand {
  if (score <= 39) return 'low'
  if (score <= 54) return 'moderate_low'
  if (score <= 69) return 'moderate'
  if (score <= 84) return 'moderate_high'
  return 'high'
}

export function buildAgreedDeclarations(
  agreedAt: string = new Date().toISOString(),
): RiskProfileDeclaration[] {
  return [
    {
      id: 'confidentiality',
      text: NDA_ACCEPTANCE_CHECKBOX_LABELS.confidentiality,
      agreed: true,
      agreed_at: agreedAt,
    },
    {
      id: 'no_offer',
      text: NDA_ACCEPTANCE_CHECKBOX_LABELS.noOffer,
      agreed: true,
      agreed_at: agreedAt,
    },
    {
      id: 'kyc_documents',
      text: NDA_ACCEPTANCE_CHECKBOX_LABELS.kycDocuments,
      agreed: true,
      agreed_at: agreedAt,
    },
    {
      id: 'adverse_media',
      text: NDA_ACCEPTANCE_CHECKBOX_LABELS.adverseMedia,
      agreed: true,
      agreed_at: agreedAt,
    },
  ]
}

export function buildRiskProfilePayload(
  answers: RiskProfilerAnswers,
  declarations: RiskProfileDeclaration[] = [],
): RiskProfileAnswersPayload {
  const score = calculateRiskProfileScore(answers)
  return {
    answers,
    totalScore: score,
    riskBand: score !== null ? getRiskProfileBand(score) : null,
    declarations,
  }
}

/** PATCH /v1/user body for risk profiler completion (matches backend schema). */
export function buildRiskProfilePatchBody(
  answers: RiskProfilerAnswers,
  declarations: RiskProfileDeclaration[] = [],
): RiskProfilePatchBody {
  return {
    risk_profile_completed: true,
    risk_profile_answers: buildRiskProfilePayload(answers, declarations),
  }
}

export function getRiskProfileBandLabel(band: RiskProfileAnswersPayload['riskBand']): string {
  if (!band) return '—'
  return RISK_BAND_LABELS[band] ?? band
}

export function getRiskAnswerLabel(questionId: string, optionId: RiskAnswerValue): string {
  if (Array.isArray(optionId)) {
    return optionId
      .map((id) => getRiskAnswerLabel(questionId, id))
      .filter(Boolean)
      .join(', ')
  }

  const question = RISK_PROFILER_QUESTIONS.find((q) => q.id === questionId)
  const option = question?.options.find((o) => o.id === optionId)
  return option?.label ?? optionId
}

export function hasRiskProfileData(profile: RiskProfileDisplay | null | undefined): boolean {
  if (!profile) return false
  return (
    profile.completed ||
    profile.riskBand !== null ||
    profile.totalScore !== null ||
    Object.keys(profile.answers).length > 0
  )
}

function parseStoredScore(payload: Record<string, unknown>): number | null {
  if (typeof payload.totalScore === 'number') return payload.totalScore
  // Legacy field from the previous average-score model
  if (typeof payload.averageScore === 'number') return payload.averageScore
  return null
}

function parseStoredBand(value: unknown): RiskProfileBand | null {
  if (typeof value !== 'string') return null
  if (value in RISK_BAND_LABELS) return value as RiskProfileBand
  return null
}

export function mapApiProfileToRiskProfile(
  data: Record<string, unknown>,
): RiskProfileDisplay | null {
  const completed = Boolean(data.risk_profile_completed ?? data.riskProfileCompleted)
  const raw = data.risk_profile_answers ?? data.riskProfileAnswers

  if (!raw || typeof raw !== 'object') {
    return completed
      ? { completed: true, answers: {}, totalScore: null, riskBand: null, declarations: [] }
      : null
  }

  const payload = raw as Record<string, unknown>
  const answers =
    payload.answers && typeof payload.answers === 'object'
      ? (payload.answers as RiskProfilerAnswers)
      : {}

  return {
    completed,
    answers,
    totalScore: parseStoredScore(payload),
    riskBand: parseStoredBand(payload.riskBand),
    declarations: Array.isArray(payload.declarations)
      ? (payload.declarations as RiskProfileDeclaration[])
      : [],
  }
}
